// A well drawn as a schematic: the flowline with the rate meter and the choke, the wellhead on the ground line, and
// the well down to the reservoir with the downhole gauges above the production packer. Each reading sits at its sensor.
// The injection path (flowline and tubing) is teal while the well injects, with dashes flowing into the well.
// Colours are tokens, so it flips with the theme.
const GROUND = 90;
const PACKER = 226;
const LINE = 'var(--color-secondary-700)';
const DASH = '—';

type Props = {
  // Formatted values with their unit; null is no reading.
  chokePct: string | null;
  rate: string | null;
  wellheadTemp: string | null;
  wellheadPressure: string | null;
  downholeTemp: string | null;
  downholePressure: string | null;
  injecting: boolean | null;
  label: string;
};

export function WellSchematic({ chokePct, rate, wellheadTemp, wellheadPressure, downholeTemp, downholePressure, injecting, label }: Props) {
  const flow = injecting === true ? 'var(--color-chart-teal)' : 'var(--color-secondary-400)';
  return (
    <svg viewBox="0 0 320 300" role="img" aria-label={label} className="max-h-[19rem] w-full">
      {/* Ground line, flowline and choke. */}
      <line x1={0} y1={GROUND} x2={320} y2={GROUND} stroke={LINE} strokeWidth={1} />
      <line x1={0} y1={70} x2={130} y2={70} stroke={flow} strokeWidth={2} />
      <path d="M 70 62 L 90 78 L 90 62 L 70 78 Z" fill="var(--color-white)" stroke={LINE} strokeWidth={1.5} />
      <Sensor x={30} y={70} />

      {/* Wellhead. */}
      <rect x={140} y={40} width={20} height={10} fill="none" stroke={LINE} strokeWidth={1.5} />
      <rect x={128} y={50} width={44} height={40} fill="var(--color-white)" stroke={LINE} strokeWidth={1.5} />
      <Sensor x={176} y={60} />
      <Sensor x={176} y={78} />

      {/* Casing, tubing, packer and downhole gauges. */}
      <line x1={136} y1={GROUND} x2={136} y2={296} stroke={LINE} strokeWidth={1.5} />
      <line x1={164} y1={GROUND} x2={164} y2={296} stroke={LINE} strokeWidth={1.5} />
      <line x1={146} y1={GROUND} x2={146} y2={280} stroke={flow} strokeWidth={2} />
      <line x1={154} y1={GROUND} x2={154} y2={280} stroke={flow} strokeWidth={2} />
      {injecting === true && <Flow />}
      <rect x={137} y={PACKER} width={8} height={7} fill={LINE} />
      <rect x={155} y={PACKER} width={8} height={7} fill={LINE} />
      <Sensor x={159} y={PACKER - 16} r={3} />
      <Sensor x={159} y={PACKER - 6} r={3} />

      {/* Reservoir and perforations. */}
      <line x1={70} y1={262} x2={250} y2={262} stroke={LINE} strokeWidth={1} strokeDasharray="5 4" />
      <line x1={70} y1={296} x2={250} y2={296} stroke={LINE} strokeWidth={1} strokeDasharray="5 4" />
      {[272, 280, 288].map(y => (
        <g key={y}>
          <line x1={124} y1={y} x2={136} y2={y} stroke={LINE} strokeWidth={1} />
          <line x1={164} y1={y} x2={176} y2={y} stroke={LINE} strokeWidth={1} />
        </g>
      ))}

      {/* Readings with leaders to their sensors. */}
      <Leader x1={80} y1={62} x2={80} y2={38} />
      <Reading x={80} y={14} anchor="middle" name="Choke" value={chokePct} />
      <Leader x1={30} y1={74} x2={30} y2={100} />
      <Reading x={4} y={112} name="Injection rate" value={rate} />
      <Leader x1={180} y1={58} x2={198} y2={42} />
      <Reading x={200} y={20} name="Wellhead temp." value={wellheadTemp} />
      <Leader x1={180} y1={80} x2={198} y2={100} />
      <Reading x={200} y={108} name="Wellhead pressure" value={wellheadPressure} />
      <Leader x1={162} y1={PACKER - 18} x2={198} y2={186} />
      <Reading x={200} y={170} name="Downhole temp." value={downholeTemp} />
      <Leader x1={162} y1={PACKER - 4} x2={198} y2={226} />
      <Reading x={200} y={210} name="Downhole pressure" value={downholePressure} />
    </svg>
  );
}

// Light dashes that move slowly along the flowline and down the tubing: CO₂ going into the well.
function Flow() {
  const dash = { stroke: 'var(--color-white)', strokeWidth: 2, strokeDasharray: '4 16', strokeOpacity: 0.8, className: 'motion-safe:animate-flow' };
  return (
    <g aria-hidden>
      <line x1={0} y1={70} x2={128} y2={70} {...dash} />
      <line x1={146} y1={GROUND} x2={146} y2={280} {...dash} />
      <line x1={154} y1={GROUND} x2={154} y2={280} {...dash} />
    </g>
  );
}

function Sensor({ x, y, r = 4 }: { x: number; y: number; r?: number }) {
  return <circle cx={x} cy={y} r={r} fill="var(--color-white)" stroke={LINE} strokeWidth={1.2} />;
}

function Leader({ x1, y1, x2, y2 }: { x1: number; y1: number; x2: number; y2: number }) {
  return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--color-secondary-400)" strokeWidth={1} />;
}

// The name above, the value below it; the value is the dash glyph without a reading.
function Reading({ x, y, name, value, anchor = 'start' }: { x: number; y: number; name: string; value: string | null; anchor?: 'start' | 'middle' }) {
  return (
    <text x={x} y={y} textAnchor={anchor}>
      <tspan x={x} fontSize={11} fill="var(--color-secondary-600)">{name}</tspan>
      <tspan x={x} dy={17} fontSize={15} fontWeight={600} fill={value == null ? 'var(--color-secondary-600)' : 'var(--color-secondary-900)'}>{value ?? DASH}</tspan>
    </text>
  );
}
